import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, FileText, Save, FileDown, User, Users, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { exportEmploymentContractToPDF } from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, saveDocumentToApplicantFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
  birth_date: string | null;
  department: string | null;
}

interface Applicant {
  id: string;
  first_name: string;
  last_name: string;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
  birth_date: string | null;
  desired_position: string | null;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export const EmploymentContractForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [personType, setPersonType] = useState<"employee" | "applicant">("employee");
  const [showSignature, setShowSignature] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  
  const [formData, setFormData] = useState({
    selectedPersonId: "",
    personName: "",
    personAddressStreet: "",
    personAddressZip: "",
    personAddressCity: "",
    personBirthDate: "",
    position: "",
    department: "",
    documentDate: new Date().toISOString().split("T")[0],
    startDate: "",
    endDate: "",
    probationPeriodMonths: 6,
    noticePeriodWeeks: 4,
    baseSalary: "",
    salaryType: "monthly",
    vacationDays: 30,
    weeklyHours: 40,
    additionalTerms: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, applicantsRes, companyRes] = await Promise.all([
        supabase.from("employees").select("id, name, position, address_street, address_zip, address_city, birth_date, department").is("archived_at", null),
        supabase.from("applicants").select("id, first_name, last_name, address_street, address_zip, address_city, birth_date, desired_position").neq("current_stage", "rejected"),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single()
      ]);

      if (employeesRes.data) setEmployees(employeesRes.data);
      if (applicantsRes.data) setApplicants(applicantsRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelect = (personId: string) => {
    setFormData(prev => ({ ...prev, selectedPersonId: personId }));

    if (personType === "employee") {
      const employee = employees.find(e => e.id === personId);
      if (employee) {
        setFormData(prev => ({
          ...prev,
          selectedPersonId: personId,
          personName: employee.name,
          personAddressStreet: employee.address_street || "",
          personAddressZip: employee.address_zip || "",
          personAddressCity: employee.address_city || "",
          personBirthDate: employee.birth_date || "",
          position: employee.position || "",
          department: employee.department || "",
        }));
      }
    } else {
      const applicant = applicants.find(a => a.id === personId);
      if (applicant) {
        setFormData(prev => ({
          ...prev,
          selectedPersonId: personId,
          personName: `${applicant.first_name} ${applicant.last_name}`,
          personAddressStreet: applicant.address_street || "",
          personAddressZip: applicant.address_zip || "",
          personAddressCity: applicant.address_city || "",
          personBirthDate: applicant.birth_date || "",
          position: applicant.desired_position || "",
          department: "",
        }));
      }
    }
  };

  const handleSave = async (signatureUrl?: string) => {
    if (!user) return;
    if (!formData.personName || !formData.startDate || !formData.baseSalary) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "employment_contract",
        employee_id: personType === "employee" ? formData.selectedPersonId : null,
        applicant_id: personType === "applicant" ? formData.selectedPersonId : null,
        person_name: formData.personName,
        person_address_street: formData.personAddressStreet,
        person_address_zip: formData.personAddressZip,
        person_address_city: formData.personAddressCity,
        person_birth_date: formData.personBirthDate || null,
        position: formData.position,
        department: formData.department,
        start_date: formData.startDate,
        end_date: formData.endDate || null,
        probation_period_months: formData.probationPeriodMonths,
        notice_period_weeks: formData.noticePeriodWeeks,
        base_salary: parseFloat(formData.baseSalary),
        salary_type: formData.salaryType,
        vacation_days: formData.vacationDays,
        weekly_hours: formData.weeklyHours,
        custom_text: formData.additionalTerms,
        signature_url: signatureUrl,
        signed_at: signatureUrl ? new Date().toISOString() : null,
        signed_by: signatureUrl ? (personType === "employee" ? formData.selectedPersonId : null) : null,
        status: signatureUrl ? "signed" : "draft",
        created_by: user.id,
      });

      if (error) throw error;

      // If employee selected and has salary info, update employee record
      if (personType === "employee" && formData.selectedPersonId) {
        await supabase.from("employees").update({
          position: formData.position,
          department: formData.department,
          vacation_days: formData.vacationDays,
          weekly_hours: formData.weeklyHours,
        }).eq("id", formData.selectedPersonId);

        // Add salary history entry
        await supabase.from("salary_history").insert({
          employee_id: formData.selectedPersonId,
          effective_date: formData.startDate,
          base_salary: parseFloat(formData.baseSalary),
          salary_type: formData.salaryType,
          currency: "EUR",
          reason: "Arbeitsvertrag",
          created_by: user.id,
        });
      }

      // Create + archive PDF to documents (employee/applicant)
      if (formData.selectedPersonId) {
        try {
          const pdfDoc = await exportEmploymentContractToPDF(
            {
              personName: formData.personName,
              personAddress: `${formData.personAddressStreet}, ${formData.personAddressZip} ${formData.personAddressCity}`,
              personBirthDate: formData.personBirthDate,
              position: formData.position,
              department: formData.department,
              documentDate: formData.documentDate,
              startDate: formData.startDate,
              endDate: formData.endDate,
              probationPeriodMonths: formData.probationPeriodMonths,
              noticePeriodWeeks: formData.noticePeriodWeeks,
              baseSalary: parseFloat(formData.baseSalary),
              salaryType: formData.salaryType,
              vacationDays: formData.vacationDays,
              weeklyHours: formData.weeklyHours,
              additionalTerms: formData.additionalTerms,
              companyName: companySettings?.name || "",
              companyAddress: companySettings
                ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}`
                : "",
              signatureUrl,
            },
            false
          );

          if (personType === "employee") {
            const category = getDocumentCategory("employment_contract");
            const typeName = getDocumentTypeName("employment_contract");
            const result = await saveDocumentToEmployeeFile(
              formData.selectedPersonId,
              `${typeName} - ${formData.position}`,
              category,
              pdfDoc,
              user.id
            );
            if (result.success) {
              toast.success("Arbeitsvertrag gespeichert und in Dokumente abgelegt");
            } else {
              console.warn("Dokument konnte nicht gespeichert werden:", result.error);
              toast.error("Arbeitsvertrag gespeichert, PDF-Ablage fehlgeschlagen");
            }
          } else {
            const result = await saveDocumentToApplicantFile(
              formData.selectedPersonId,
              `Arbeitsvertrag - ${formData.position}`,
              "other",
              pdfDoc,
              user.id
            );
            if (result.success) {
              toast.success("Arbeitsvertrag gespeichert und beim Bewerber abgelegt");
            } else {
              console.warn("Bewerber-Dokument konnte nicht gespeichert werden:", result.error);
              toast.error("Arbeitsvertrag gespeichert, PDF-Ablage fehlgeschlagen");
            }
          }
        } catch (e) {
          console.error("Error archiving PDF:", e);
          toast.error("Arbeitsvertrag gespeichert, PDF-Ablage fehlgeschlagen");
        }
      } else {
        toast.success("Arbeitsvertrag wurde gespeichert");
      }
      setShowSignature(false);
      resetForm();
    } catch (error) {
      console.error("Error saving contract:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const buildPdfPayload = () => ({
    personName: formData.personName,
    personAddress: `${formData.personAddressStreet}, ${formData.personAddressZip} ${formData.personAddressCity}`,
    personBirthDate: formData.personBirthDate,
    position: formData.position,
    department: formData.department,
    documentDate: formData.documentDate,
    startDate: formData.startDate,
    endDate: formData.endDate,
    probationPeriodMonths: formData.probationPeriodMonths,
    noticePeriodWeeks: formData.noticePeriodWeeks,
    baseSalary: parseFloat(formData.baseSalary) || 0,
    salaryType: formData.salaryType,
    vacationDays: formData.vacationDays,
    weeklyHours: formData.weeklyHours,
    additionalTerms: formData.additionalTerms,
    companyName: companySettings?.name || "",
    companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
  });

  const handlePreview = async () => {
    if (!formData.personName) {
      toast.error("Bitte wählen Sie zuerst eine Person aus");
      return;
    }
    try {
      const pdfDoc = await exportEmploymentContractToPDF(buildPdfPayload(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.personName || !formData.startDate || !formData.baseSalary) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    try {
      // Pass saveToFile: false to get the PDF document without auto-downloading
      const pdfDoc = await exportEmploymentContractToPDF({
        personName: formData.personName,
        personAddress: `${formData.personAddressStreet}, ${formData.personAddressZip} ${formData.personAddressCity}`,
        personBirthDate: formData.personBirthDate,
        position: formData.position,
        department: formData.department,
        documentDate: formData.documentDate,
        startDate: formData.startDate,
        endDate: formData.endDate,
        probationPeriodMonths: formData.probationPeriodMonths,
        noticePeriodWeeks: formData.noticePeriodWeeks,
        baseSalary: parseFloat(formData.baseSalary),
        salaryType: formData.salaryType,
        vacationDays: formData.vacationDays,
        weeklyHours: formData.weeklyHours,
        additionalTerms: formData.additionalTerms,
        companyName: companySettings?.name || "",
        companyAddress: companySettings ? `${companySettings.street || ""} ${companySettings.house_number || ""}, ${companySettings.postal_code || ""} ${companySettings.city || ""}` : "",
      }, false);

      // Save to employee documents if employee is selected
      if (personType === "employee" && formData.selectedPersonId && user) {
        const category = getDocumentCategory('employment_contract');
        const typeName = getDocumentTypeName('employment_contract');
        const result = await saveDocumentToEmployeeFile(
          formData.selectedPersonId,
          `${typeName} - ${formData.position}`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("PDF wurde erstellt und in den Mitarbeiter-Dokumenten gespeichert");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
        }
      }

      // Always download the PDF for the user
      pdfDoc.save(`Arbeitsvertrag_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedPersonId: "",
      personName: "",
      personAddressStreet: "",
      personAddressZip: "",
      personAddressCity: "",
      personBirthDate: "",
      position: "",
      department: "",
      documentDate: new Date().toISOString().split("T")[0],
      startDate: "",
      endDate: "",
      probationPeriodMonths: 6,
      noticePeriodWeeks: 4,
      baseSalary: "",
      salaryType: "monthly",
      vacationDays: 30,
      weeklyHours: 40,
      additionalTerms: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Neuen Arbeitsvertrag erstellen
        </CardTitle>
        <CardDescription>
          Erstellen Sie einen rechtlich konformen Arbeitsvertrag für das Handwerk
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Person Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Person auswählen</h4>
          <div className="flex gap-4">
            <Button
              variant={personType === "employee" ? "default" : "outline"}
              onClick={() => setPersonType("employee")}
              className="flex items-center gap-2"
            >
              <User className="h-4 w-4" />
              Mitarbeiter
            </Button>
            <Button
              variant={personType === "applicant" ? "default" : "outline"}
              onClick={() => setPersonType("applicant")}
              className="flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              Bewerber
            </Button>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>{personType === "employee" ? "Mitarbeiter" : "Bewerber"} auswählen *</Label>
              <Select value={formData.selectedPersonId} onValueChange={handlePersonSelect}>
                <SelectTrigger>
                  <SelectValue placeholder={`${personType === "employee" ? "Mitarbeiter" : "Bewerber"} wählen...`} />
                </SelectTrigger>
                <SelectContent>
                  {personType === "employee" 
                    ? employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))
                    : applicants.map(app => (
                        <SelectItem key={app.id} value={app.id}>{`${app.first_name} ${app.last_name}`}</SelectItem>
                      ))
                  }
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name *</Label>
              <Input 
                value={formData.personName} 
                onChange={(e) => setFormData(prev => ({ ...prev, personName: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Straße</Label>
              <Input 
                value={formData.personAddressStreet} 
                onChange={(e) => setFormData(prev => ({ ...prev, personAddressStreet: e.target.value }))}
              />
            </div>
            <div>
              <Label>PLZ</Label>
              <Input 
                value={formData.personAddressZip} 
                onChange={(e) => setFormData(prev => ({ ...prev, personAddressZip: e.target.value }))}
              />
            </div>
            <div>
              <Label>Stadt</Label>
              <Input 
                value={formData.personAddressCity} 
                onChange={(e) => setFormData(prev => ({ ...prev, personAddressCity: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label>Geburtsdatum</Label>
            <Input 
              type="date"
              value={formData.personBirthDate} 
              onChange={(e) => setFormData(prev => ({ ...prev, personBirthDate: e.target.value }))}
            />
          </div>
        </div>

        {/* Contract Details */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Vertragsdaten</h4>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Dokumentendatum</Label>
              <Input 
                type="date"
                value={formData.documentDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Position *</Label>
              <Input 
                value={formData.position} 
                onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                placeholder="z.B. Elektriker, Bauleiter..."
              />
            </div>
            <div>
              <Label>Abteilung</Label>
              <Input 
                value={formData.department} 
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Arbeitsbeginn *</Label>
              <Input 
                type="date"
                value={formData.startDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Befristet bis (optional)</Label>
              <Input 
                type="date"
                value={formData.endDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Probezeit (Monate)</Label>
              <Input 
                type="number"
                value={formData.probationPeriodMonths} 
                onChange={(e) => setFormData(prev => ({ ...prev, probationPeriodMonths: parseInt(e.target.value) || 6 }))}
              />
            </div>
            <div>
              <Label>Kündigungsfrist (Wochen)</Label>
              <Input 
                type="number"
                value={formData.noticePeriodWeeks} 
                onChange={(e) => setFormData(prev => ({ ...prev, noticePeriodWeeks: parseInt(e.target.value) || 4 }))}
              />
            </div>
          </div>
        </div>

        {/* Compensation */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Vergütung</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Grundgehalt (EUR) *</Label>
              <Input 
                type="number"
                value={formData.baseSalary} 
                onChange={(e) => setFormData(prev => ({ ...prev, baseSalary: e.target.value }))}
                placeholder="z.B. 3500"
              />
            </div>
            <div>
              <Label>Gehaltsart</Label>
              <Select value={formData.salaryType} onValueChange={(value) => setFormData(prev => ({ ...prev, salaryType: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monatlich</SelectItem>
                  <SelectItem value="hourly">Stündlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Urlaubstage pro Jahr</Label>
              <Input 
                type="number"
                value={formData.vacationDays} 
                onChange={(e) => setFormData(prev => ({ ...prev, vacationDays: parseInt(e.target.value) || 30 }))}
              />
            </div>
            <div>
              <Label>Wochenstunden</Label>
              <Input 
                type="number"
                value={formData.weeklyHours} 
                onChange={(e) => setFormData(prev => ({ ...prev, weeklyHours: parseFloat(e.target.value) || 40 }))}
              />
            </div>
          </div>
        </div>

        {/* Additional Terms */}
        <div className="space-y-4">
          <Label>Zusätzliche Vereinbarungen</Label>
          <Textarea 
            value={formData.additionalTerms} 
            onChange={(e) => setFormData(prev => ({ ...prev, additionalTerms: e.target.value }))}
            placeholder="Weitere Vereinbarungen, Sonderregelungen..."
            rows={4}
          />
        </div>

        {/* Signature Section */}
        {showSignature && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h4 className="font-medium">Unterschrift des Arbeitnehmers</h4>
            <SignatureCanvas
              onSave={async (signatureDataUrl) => {
                // Upload signature and save
                const blob = await fetch(signatureDataUrl).then(r => r.blob());
                const fileName = `contract-signature-${Date.now()}.png`;
                const { data: uploadData, error: uploadError } = await supabase.storage
                  .from("employee-profiles")
                  .upload(`signatures/${fileName}`, blob);

                if (uploadError) throw uploadError;

                const { data: urlData } = supabase.storage
                  .from("employee-profiles")
                  .getPublicUrl(`signatures/${fileName}`);

                await handleSave(urlData.publicUrl);
                setShowSignature(false);
              }}
              title="Unterschrift Mitarbeiter/Bewerber"
              description="Bitte unterschreiben Sie zur Bestätigung des Arbeitsvertrags"
              confirmationText="Mit Ihrer Unterschrift akzeptieren Sie die Vertragsbedingungen."
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handlePreview} disabled={saving} className="gap-2">
            <Eye className="h-4 w-4" />
            Vorschau
          </Button>
          <Button onClick={() => handleSave()} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Als Entwurf speichern
          </Button>
          <Button variant="outline" onClick={() => setShowSignature(true)} className="gap-2">
            <FileText className="h-4 w-4" />
            Mit Unterschrift speichern
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileDown className="h-4 w-4" />
            PDF exportieren
          </Button>
        </div>
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Arbeitsvertrag"
        fileName={`Arbeitsvertrag_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
